import { useMemo } from 'react'

import { useCollection } from '@/shared/firebase/hooks/useCollection'
import { Reservation } from '@monorepo/functions/src/types/reservations'

import { isReservationActive } from '../model/isReservationActive'

interface UseClientReservationsReturn {
    reservations: Reservation[]
    activeReservations: number
    totalSpent: number
    lastReservationDate: string | null
    isLoading: boolean
}

export const useClientReservations = (clientId: string): UseClientReservationsReturn => {
    const [whereClauses] = useMemo(() => {
        return [[['clientId', '==', clientId]]]
    }, [clientId])

    const { results: reservations, isLoading } = useCollection<Reservation>({
        path: 'reservations',
        where: whereClauses as any,
        orderBy: ['createdAt', 'desc'],
    })

    const { activeReservations, totalSpent, lastReservationDate } = useMemo(() => {
        const activeReservations = reservations.filter(isReservationActive)

        const totalSpent = reservations.reduce((acc, reservation) => {
            if (reservation.status === 'cancelled') return acc
            return acc + (reservation.totalPrice ?? 0)
        }, 0)

        const lastDate =
            reservations.length > 0
                ? reservations.reduce((latest, reservation) => {
                      const date = reservation.type === 'hotel' ? reservation.checkOutDate : reservation.date
                      return date > latest ? date : latest
                  }, '')
                : null

        return {
            activeReservations: activeReservations.length,
            totalSpent: totalSpent,
            lastReservationDate: lastDate,
        }
    }, [reservations])

    return {
        reservations,
        activeReservations,
        totalSpent,
        lastReservationDate,
        isLoading,
    }
}
